import React, { useEffect, useState } from 'react';
import api from '../api/axios';

// (G3) Ish kalendari - tenant bo'yicha. Backend: app/routers/calendar_days.py
// (faqat buxgalteriya / rahbariyat). To'lov muddatlari shanba/yakshanba va shu
// ro'yxatdagi bayram / ko'chirilgan dam olish kunlarini o'tkazib yuboradi;
// "ko'chirilgan ish kuni" (shanba/yakshanba) ish kuni deb sanaladi.
// Qat'iy bayramlar (2026-2027) migratsiyada yozilgan; hayitlar va hukumat
// ko'chirgan kunlar shu yerda qo'shiladi.
type Kind = 'holiday' | 'day_off' | 'workday';
interface CalendarDay { id: number; day: string; name: string; kind: Kind; weekday: number; }

export const DAY_KIND: Record<Kind, { label: string; color: string }> = {
  holiday: { label: 'Bayram', color: 'bg-red-100 text-red-700' },
  day_off: { label: "Ko'chirilgan dam olish kuni", color: 'bg-orange-100 text-orange-800' },
  workday: { label: "Ko'chirilgan ish kuni", color: 'bg-green-100 text-green-700' },
};
const WEEKDAY = ['Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh', 'Ya'];
const fmtDate = (s: string) => s.split('-').reverse().join('.');

const HolidayCalendar: React.FC<{ canFull: boolean }> = ({ canFull }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [days, setDays] = useState<CalendarDay[]>([]);
  const [form, setForm] = useState<{ day: string; name: string; kind: Kind }>({ day: '', name: '', kind: 'holiday' });
  const [check, setCheck] = useState({ start: '', days: '3', result: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setDays((await api.get<CalendarDay[]>('/calendar-days', { params: { year } })).data); } catch { setDays([]); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [year]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError('');
    try { await fn(); await load(); } catch (e: any) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : "Xato - sana va nomni tekshiring");
    } finally { setBusy(false); }
  };
  const add = () => {
    if (!form.day || !form.name.trim()) { setError('Sana va nomini kiriting'); return; }
    run(async () => { await api.post('/calendar-days', { ...form, name: form.name.trim() }); setForm({ day: '', name: '', kind: form.kind }); });
  };
  const rename = (d: CalendarDay) => {
    const name = window.prompt(`${fmtDate(d.day)} - nomi:`, d.name);
    if (!name || !name.trim()) return;
    run(() => api.put(`/calendar-days/${d.id}`, { name: name.trim() }));
  };
  const remove = (d: CalendarDay) => {
    if (!window.confirm(`${fmtDate(d.day)} (${d.name}) kalendardan o'chirilsinmi?`)) return;
    run(() => api.delete(`/calendar-days/${d.id}`));
  };
  const preview = async () => {
    if (!check.start) return;
    try {
      const r = await api.get('/calendar-days/add-working-days', { params: { start: check.start, days: Number(check.days || 0) } });
      setCheck({ ...check, result: r.data.result });
    } catch { setCheck({ ...check, result: '' }); }
  };

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-semibold text-gray-800">📅 Ish kalendari (dam olish va bayram kunlari)</h3>
        <div className="flex items-center gap-1 text-sm">
          <button onClick={() => setYear(year - 1)} className="px-2 py-1 border rounded hover:bg-gray-50">‹</button>
          <span className="px-2 font-medium">{year}</span>
          <button onClick={() => setYear(year + 1)} className="px-2 py-1 border rounded hover:bg-gray-50">›</button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        To&apos;lov muddatlari (bank kuni = ish kuni) shanba, yakshanba va quyidagi bayram / ko&apos;chirilgan dam olish kunlarini
        o&apos;tkazib yuboradi; &quot;ko&apos;chirilgan ish kuni&quot; ish kuni deb sanaladi. Hisobdagi muddatni ✎ bilan qo&apos;lda o&apos;zgartirish mumkin.
      </p>
      {canFull && (
        <div className="flex flex-wrap items-end gap-2 mb-3 text-sm">
          <input type="date" value={form.day} onChange={e => setForm({ ...form, day: e.target.value })} className="border rounded px-2 py-1" />
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nomi (masalan: Ramazon hayiti)"
            className="border rounded px-2 py-1 w-64" />
          <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value as Kind })} className="border rounded px-2 py-1">
            {(Object.keys(DAY_KIND) as Kind[]).map(k => <option key={k} value={k}>{DAY_KIND[k].label}</option>)}
          </select>
          <button onClick={add} disabled={busy} className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50">+ Qo&apos;shish</button>
        </div>
      )}
      {error && <div className="text-red-600 text-xs mb-2">{error}</div>}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b">
            <th className="py-1 pr-2 whitespace-nowrap">Sana</th><th className="pr-2">Kun</th><th className="pr-2">Nomi</th>
            <th className="pr-2">Turi</th>{canFull && <th />}
          </tr>
        </thead>
        <tbody>
          {days.map(d => (
            <tr key={d.id} className="border-b last:border-0">
              <td className="py-1 pr-2 whitespace-nowrap">{fmtDate(d.day)}</td>
              <td className="pr-2 text-gray-500">{WEEKDAY[d.weekday]}</td>
              <td className="pr-2">{d.name}</td>
              <td className="pr-2 whitespace-nowrap"><span className={`px-2 py-0.5 rounded text-xs ${DAY_KIND[d.kind].color}`}>{DAY_KIND[d.kind].label}</span></td>
              {canFull && (
                <td className="whitespace-nowrap text-right space-x-1">
                  <button onClick={() => rename(d)} className="text-blue-600 hover:underline text-xs">✎</button>
                  <button onClick={() => remove(d)} className="text-red-600 hover:underline text-xs">O&apos;chirish</button>
                </td>
              )}
            </tr>
          ))}
          {days.length === 0 && <tr><td colSpan={5} className="py-3 text-center text-gray-400">{year} yil uchun kun yo&apos;q</td></tr>}
        </tbody>
      </table>
      <div className="mt-4 pt-3 border-t flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-600">Tekshirish:</span>
        <input type="date" value={check.start} onChange={e => setCheck({ ...check, start: e.target.value, result: '' })} className="border rounded px-2 py-1" />
        <span>+</span>
        <input type="number" min="0" value={check.days} onChange={e => setCheck({ ...check, days: e.target.value, result: '' })}
          className="border rounded px-2 py-1 w-16" />
        <span>ish (bank) kuni</span>
        <button onClick={preview} className="px-2 py-1 border rounded hover:bg-gray-50">=</button>
        {check.result && <span className="font-medium">{fmtDate(check.result)}</span>}
      </div>
    </div>
  );
};

export default HolidayCalendar;
